/**
 * GuardGrade Score Utilities
 *
 * Internal scores are 0-100. Public GuardGrade scores are 300-900.
 * This module handles all conversions and risk label logic.
 */

export const GG_MIN = 300;
export const GG_MAX = 900;
export const GG_BASELINE = 720;
export const GG_GREEN_THRESHOLD = 750;
export const GG_YELLOW_THRESHOLD = 650;
export const SCORE_MULTIPLIER = 6;

/** Convert an internal 0-100 score to the public 300-900 GuardGrade scale. */
export function toGGScore(internalScore: number | string): number {
  const n = Number(internalScore) || 0;
  return Math.max(GG_MIN, Math.min(GG_MAX, Math.round(GG_MIN + n * SCORE_MULTIPLIER)));
}

/** Convert an internal delta to the public scale. */
export function toGGDelta(internalDelta: number | string): number {
  return Math.round(Number(internalDelta) * SCORE_MULTIPLIER);
}

/** Status labels per Feature 3 (locked June 4, 2026).
 *
 * Replaces the legally aggressive "Low Risk / Moderate Risk / Elevated Risk"
 * with neutral observational language.
 */
export type StatusLabel =
  | 'Strong Observed Performance'
  | 'Stable Observed Performance'
  | 'Developing / Mixed Performance';

/** Get the status label for a GuardGrade score (300-900).
 *
 * @param ggScore — public-scale score (300-900)
 * @param opts.confidenceLabel — F2 Confidence label for this entity
 * @param opts.isPublicLayer1 — true ONLY when rendering the unauthenticated
 *   public profile page (F1).  When true AND confidence is Low, the label
 *   caps at "Developing / Mixed Performance" regardless of numeric score
 *   (spec §161).  In-app dashboards do NOT cap.
 */
export function getStatusLabel(
  ggScore: number,
  opts?: { confidenceLabel?: 'High' | 'Medium' | 'Low'; isPublicLayer1?: boolean }
): StatusLabel {
  if (opts?.isPublicLayer1 && opts?.confidenceLabel === 'Low') {
    return 'Developing / Mixed Performance';
  }
  if (ggScore >= GG_GREEN_THRESHOLD) return 'Strong Observed Performance';
  if (ggScore >= GG_YELLOW_THRESHOLD) return 'Stable Observed Performance';
  return 'Developing / Mixed Performance';
}

/** Get the color key for a GuardGrade score. */
export function getRiskColor(ggScore: number): 'green' | 'yellow' | 'red' {
  if (ggScore >= GG_GREEN_THRESHOLD) return 'green';
  if (ggScore >= GG_YELLOW_THRESHOLD) return 'yellow';
  return 'red';
}

/** Get the color for an internal 0-100 score (convenience shortcut). */
export function getScoreColor(internalScore: number | string): 'green' | 'yellow' | 'red' {
  return getRiskColor(toGGScore(internalScore));
}

/** Convert a GuardGrade score to a 0-100 progress percentage for bars/gauges. */
export function getScoreProgressPct(ggScore: number): number {
  return Math.max(0, Math.min(100, ((ggScore - GG_MIN) / (GG_MAX - GG_MIN)) * 100));
}

/** Tailwind color classes based on risk color. */
export function getScoreColorClass(color: 'green' | 'yellow' | 'red'): string {
  switch (color) {
    case 'green': return 'text-emerald-600';
    case 'yellow': return 'text-amber-600';
    case 'red': return 'text-red-600';
  }
}

export function getScoreBgClass(color: 'green' | 'yellow' | 'red'): string {
  switch (color) {
    case 'green': return 'bg-emerald-500';
    case 'yellow': return 'bg-amber-500';
    case 'red': return 'bg-red-500';
  }
}
